/**
 * Deployment API Service
 *
 * API methods for deployment operations including listing, creation, and rollback.
 * Updated to use standardized API response format.
 */

import apiClient from './client';
import type { ApiSuccessResponse, ApiCollectionResponse, ApiActionResponse } from '../types/api.types';
import type {
  DeploymentSanitized,
  DeploymentListQuery,
  DeploymentUpdateDTO,
  DeploymentAddCustomTagsDTO,
  DeploymentRollbackDTO,
} from '../types/deployment.types';

// ============================================================================
// DEPLOYMENT SERVICE
// ============================================================================

export const deploymentService = {
  /**
   * List all deployments for an application
   */
  listDeployments: async (
    tenantId: string,
    applicationId: string,
    query?: DeploymentListQuery
  ): Promise<DeploymentSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<DeploymentSanitized>>(
      `/tenants/${tenantId}/applications/${applicationId}/deployments`,
      {
        params: query,
      }
    );
    return response.data.data;
  },

  /**
   * Get deployment by ID
   */
  getDeployment: async (
    tenantId: string,
    applicationId: string,
    deploymentId: string
  ): Promise<DeploymentSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<DeploymentSanitized>>(
      `/tenants/${tenantId}/applications/${applicationId}/deployments/${deploymentId}`
    );
    return response.data.data;
  },

  /**
   * Delete deployment
   * Note: Cannot delete currently active deployment
   */
  deleteDeployment: async (
    tenantId: string,
    applicationId: string,
    deploymentId: string
  ): Promise<{ message: string }> => {
    const response = await apiClient.delete<ApiActionResponse>(
      `/tenants/${tenantId}/applications/${applicationId}/deployments/${deploymentId}`
    );
    return { message: response.data.message };
  },

  /**
   * Update deployment (e.g., set version tag)
   * Note: Version tag is immutable once set
   */
  updateDeployment: async (
    tenantId: string,
    deploymentId: string,
    data: DeploymentUpdateDTO
  ): Promise<DeploymentSanitized> => {
    const response = await apiClient.put<ApiSuccessResponse<DeploymentSanitized>>(
      `/tenants/${tenantId}/deployments/${deploymentId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Add custom tags to deployment (append-only)
   * Note: Max 10 tags per deployment, cannot remove tags once added
   */
  addCustomTags: async (
    tenantId: string,
    deploymentId: string,
    data: DeploymentAddCustomTagsDTO
  ): Promise<DeploymentSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<DeploymentSanitized>>(
      `/tenants/${tenantId}/deployments/${deploymentId}/tags`,
      data
    );
    return response.data.data;
  },

  /**
   * Activate deployment (make it the current active deployment for the application)
   */
  activateDeployment: async (
    tenantId: string,
    deploymentId: string
  ): Promise<DeploymentSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<DeploymentSanitized>>(
      `/tenants/${tenantId}/deployments/${deploymentId}/activate`
    );
    return response.data.data;
  },

  /**
   * Rollback deployment
   * Note: Cannot rollback currently active deployment
   */
  rollbackDeployment: async (
    tenantId: string,
    deploymentId: string,
    data: DeploymentRollbackDTO
  ): Promise<DeploymentSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<DeploymentSanitized>>(
      `/tenants/${tenantId}/deployments/${deploymentId}/rollback`,
      data
    );
    return response.data.data;
  },
};
